import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { Search, ArrowRight, Lock } from 'lucide-react';
import TradesPurpleLayout from '../components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import LoggedInLandingPage from './LoggedInLandingPage';

export default function LandingPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // For the fixed scroll indicator with hide on scroll
  const scrollToSection = () => {
    document.getElementById('benefits-section')?.scrollIntoView({behavior: 'smooth'});
  };

  // UK postcode first half validation (outward code)
  const validateOutwardCode = useCallback((code: string) => {
    // Simple regex for UK outward code validation
    const outwardCodeRegex = /^[A-Z]{1,2}[0-9]{1,2}$/i;
    return outwardCodeRegex.test(code);
  }, []);

  // Process the postcode and navigate if valid
  const processPostcode = useCallback((postcodeValue: string) => {
    // Normalize the postcode (remove spaces, uppercase)
    let normalizedPostcode = postcodeValue.trim().toUpperCase();

    // Handle full postcodes by extracting just the outward code (first part)
    // UK postcodes follow pattern: AA9A 9AA or A9A 9AA or A9 9AA or AA9 9AA
    // We just want the part before the space
    if (normalizedPostcode.includes(' ')) {
      normalizedPostcode = normalizedPostcode.split(' ')[0];
    } else if (normalizedPostcode.length > 4) {
      // If there's no space but it's longer than 4 chars, it might be a full postcode without space
      // Try to extract just the outward code (typically 3-4 characters)
      normalizedPostcode = normalizedPostcode.substring(0, 4);
    }

    if (!normalizedPostcode) {
      setError('Please enter your postcode');
      return false;
    }

    if (!validateOutwardCode(normalizedPostcode)) {
      setError('Please enter a valid postcode area (e.g. "HP22")');
      return false;
    }

    // Clear any previous errors
    setError(null);

    // Navigate directly to the postcode route - ALWAYS using the clean routes
    // Any access via app.househeld.co/tradespurple will be redirected by Cloudflare
    navigate(`/${normalizedPostcode}`);

    return true;
  }, [navigate, setError, validateOutwardCode]);

  // Handle form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    processPostcode(postcode);
  };

  // All useEffect hooks must be declared at top level, not after conditionals

  // Set up scroll listener to hide the indicator - only applies to anonymous page
  useEffect(() => {
    // Skip the effect for authenticated users
    if (isAuthenticated) return;

    const handleScroll = () => {
      const benefitsSection = document.getElementById('benefits-section');
      if (benefitsSection) {
        const rect = benefitsSection.getBoundingClientRect();
        // Hide the indicator if the benefits section is in view
        setShowScrollIndicator(rect.top > window.innerHeight * 0.8);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated]);

  // Removed auto-navigation effect - now requires users to press Enter

  // Early return for authenticated users
  if (isAuthenticated) {
    return <LoggedInLandingPage />;
  }

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Save and share trusted tradespeople</title>
        <meta name="description" content="Save, contact and share the tradespeople you trust. Free, local, fast." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta name="theme-color" content="#7e22ce" />
      </Helmet>

      {/* Fixed scroll indicator with "See how it works" text - conditionally shown */}
      {showScrollIndicator && (
        <div
          className="fixed bottom-6 left-0 right-0 flex flex-col items-center justify-center animate-pulse cursor-pointer z-10 group"
          onClick={scrollToSection}
        >
          <p className="text-xs text-purple-600 mb-1 font-medium">See how it works</p>
          <svg className="h-6 w-6 text-purple-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      )}

      {/* Main content wrapper - full height with flex layout */}
      <div className="flex flex-col min-h-[calc(100vh-250px)] md:min-h-[calc(100vh-350px)]">
        {/* Centered content */}
        <div className="flex flex-col items-center">

          {/* Search card - centered on desktop */}
          <Card className="w-full md:w-4/5 max-w-lg border-2 border-purple-900 rounded-xl shadow-lg">
            <CardContent className="p-5 md:p-6">
              <form onSubmit={handleSearch}>
                <div className="space-y-2">
                  <div>
                    <div className="flex space-x-2">
                      <div className="relative flex-grow">
                        <Input
                          id="postcode"
                          placeholder="Enter postcode"
                          className="border-2 border-purple-900 pl-10 py-5 text-lg placeholder:text-purple-500"
                          value={postcode}
                          onChange={(e) => {
                            // Allow full postcodes and uppercase them
                            const value = e.target.value.toUpperCase();
                            setPostcode(value);
                          }}
                          aria-label="Enter your postcode area"
                          autoComplete="postal-code"
                          type="text"
                          name="postcode"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <Search className="h-5 w-5 text-purple-700" />
                        </div>

                        {/* Lock icon with tooltip - properly positioned */}
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center group">
                          <Lock className="h-4 w-4 text-purple-700 opacity-70" />
                          <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 right-0 -top-16 bg-white border border-purple-200 rounded-md shadow-md p-2 w-60 text-xs text-gray-700 pointer-events-none">
                            Your postcode is only used to find local tradespeople. We don't store or share your details.
                          </div>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="flex items-center justify-center py-5 bg-purple-700 hover:bg-purple-800 text-white font-medium px-5"
                      >
                        <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                    {error && (
                      <p className="mt-2 text-red-600 text-sm">{error}</p>
                    )}

                    {/* New section - OR divider with CTA button */}
                    <div className="mt-5 text-center">
                      <div className="relative flex items-center justify-center">
                        <div className="border-t border-purple-200 absolute w-full"></div>
                        <span className="bg-white px-2 text-sm text-purple-500 relative">OR</span>
                      </div>
                      <Link
                        to="/auth/signup"
                        className="mt-4 inline-flex items-center justify-center px-5 py-3 bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 w-full"
                      >
                        Add my first tradesperson
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Benefit pills - moved below search box */}
          <div className="flex flex-wrap justify-center gap-2 mt-4 mb-6">
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Free
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Local
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Fast
            </span>
          </div>

          {/* Spacer to push content away from fixed arrow */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* How It Works section */}
      <div id="benefits-section" className="mt-4 md:mt-10 space-y-3">
        <h2 className="text-xl font-bold text-center mb-4" style={{ color: "#5D4A9C" }}>How It Works</h2>
        <div className="flex items-start p-4 border border-purple-200 rounded-lg bg-purple-50">
          <div className="rounded-lg bg-purple-700 p-2 mr-3 shadow-md flex-shrink-0 flex items-center justify-center w-9 h-9">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium" style={{ color: "#5D4A9C" }}>Community Sharing</h3>
            <p className="text-sm text-purple-700 mt-1">
              Your neighbours list the tradespeople they trust
            </p>
          </div>
        </div>

        <div className="flex items-start p-4 border border-purple-200 rounded-lg bg-purple-50">
          <div className="rounded-lg bg-purple-700 p-2 mr-3 shadow-md flex-shrink-0 flex items-center justify-center w-9 h-9">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium" style={{ color: "#5D4A9C" }}>Local Discovery</h3>
            <p className="text-sm text-purple-700 mt-1">
              Your postcode lets you see the most recommended
            </p>
          </div>
        </div>

        <div className="flex items-start p-4 border border-purple-200 rounded-lg bg-purple-50">
          <div className="rounded-lg bg-purple-700 p-2 mr-3 shadow-md flex-shrink-0 flex items-center justify-center w-9 h-9">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium" style={{ color: "#5D4A9C" }}>Direct Connection</h3>
            <p className="text-sm text-purple-700 mt-1">
              Get in touch with them straight away
            </p>
          </div>
        </div>

        {/* Sign up CTA after explaining benefits */}
        <div className="mt-6 p-4 border-2 border-purple-400 rounded-xl bg-purple-50">
          <div className="text-center">
            <h3 className="font-bold text-lg mb-2" style={{ color: "#5D4A9C" }}>Ready to create your own list?</h3>
            <p className="text-purple-700 mb-3">
              Join TradesPurple to save your trusted tradespeople and share them with your neighbours
            </p>
            <Link
              to="/auth/signup"
              className="inline-block px-5 py-3 bg-purple-700 hover:bg-purple-800 text-white font-medium rounded-lg transition shadow-sm"
            >
              Sign up - It's Free
            </Link>
          </div>
        </div>
      </div>
    </TradesPurpleLayout>
  );
}
